import axios from 'axios'
import { API_BASE_URL } from 'src/constants/environment'
import { requestByGET } from 'lib/requests'

import { mapPaymentMethods } from './mappers'
import { buildPathParams } from 'lib/utils/url'
import {
  buildCreatePurchaseRequest,
  buildPurchaseByIdRequest,
  buildPurchasePaymentMethodByIdRequest,
  buildUpdatePurchaseToBodyRequest,
  buildConfirmPurchaseBodyRequest,
  buildConfirmWorldlinePurchaseBodyRequest,
  buildConfirmApplePayPurchaseBodyRequest,
  buildCreateAliasBodyRequest,
} from './utils'

export const OPTIONS = {
  timeout: 30000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
}

const OPTIONS_GET = {
  timeout: 30000,
  headers: { Accept: 'application/json' },
}

const CONFIRMATION_OPTIONS = {
  timeout: 120000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
}

const CONFIRMATION_OPTIONS_GET = {
  timeout: 120000,
  headers: { Accept: 'application/json' },
}

const END_POINTS = {
  CREATE_ALIAS_VOUCHER: '/paycomet/alias-voucher',
  CREATE_ALIAS: '/paycomet/alias',
  CREATE_PURCHASE: '/purchase_id',
  INSURANCE_QUOTES: '/insurance/quotes',
  PAYMENT_METHODS_PRIME: '/v1/prime/payment-methods',
  PAYMENT_METHODS: '/purchase/payment-methods',
  PURCHASE_BY_ID: '/purchase/checkout',
  PURCHASE_CONFIRMATION_APM: '/purchase/confirmation/{paymentProvider}',
  PURCHASE_CONFIRMATION: '/purchase/confirmation',
  PURCHASE_CONFIRMATION_AUTH: '/purchase/confirmation/member',
  WORLDLINE_PURCHASE_CONFIRMATION: '/v2/purchase/confirmation',
  WORLDLINE_PURCHASE_CONFIRMATION_AUTH: '/v2/purchase/confirmation/member',
  WORLDLINE_PURCHASE_CONFIRMATION_3DS: '/worldline/purchase/confirmation-3ds',
  WORLDLINE_APPLEPAY_CONFIRMATION: '/worldline/purchase/confirmation-mobile/apple_pay',
  UPDATE_PURCHASE_BY_ID: '/purchase/customer-data',
  UPDATE_PURCHASE_BY_ID_AUTH: '/purchase/customer-data/member',
  PRICE_DROP_PROTECTION_QUOTE_GENERATE: '/pdp/quote/generate',
  PURCHASE_STATUS: '/purchase/status?purchase_id={purchaseId}',
}

function deprecatedRequestByGET(endpoint, data) {
  return axios.get(`${API_BASE_URL}${endpoint}`, { params: data, ...OPTIONS }).then(res => res.data)
}

const defaultMapper = data => data

export function requestByPOST({
  hostname = API_BASE_URL,
  endpoint,
  data,
  options = OPTIONS,
  mapper = defaultMapper,
}) {
  return axios.post(`${hostname}${endpoint}`, data, options).then(res => mapper(res.data))
}

export function createPurchase(params) {
  const queryParams = buildCreatePurchaseRequest(params)
  return deprecatedRequestByGET(END_POINTS.CREATE_PURCHASE, queryParams)
}

export function getPurchaseById(params) {
  const queryParams = buildPurchaseByIdRequest(params)
  return deprecatedRequestByGET(END_POINTS.PURCHASE_BY_ID, queryParams)
}

export function updatePurchaseById(params) {
  return requestByPOST({
    endpoint: END_POINTS.UPDATE_PURCHASE_BY_ID,
    data: buildUpdatePurchaseToBodyRequest(params),
    options: OPTIONS,
    hostname: params.hostname,
  })
}
export function updatePurchaseByIdAuthorized(params) {
  return requestByPOST({
    endpoint: END_POINTS.UPDATE_PURCHASE_BY_ID_AUTH,
    data: buildUpdatePurchaseToBodyRequest(params),
    options: { ...OPTIONS, withCredentials: true },
    hostname: params.hostname,
  })
}

export function getPaymentMethods(params) {
  return requestByGET({
    endpoint: END_POINTS.PAYMENT_METHODS,
    data: buildPurchasePaymentMethodByIdRequest(params),
    mapper: mapPaymentMethods,
  })
}

export function createAlias(params) {
  return requestByPOST({
    endpoint: END_POINTS.CREATE_ALIAS,
    data: buildCreateAliasBodyRequest(params),
    hostname: params.hostname,
  })
}

export function createAliasVoucher(params) {
  return requestByPOST({
    endpoint: END_POINTS.CREATE_ALIAS_VOUCHER,
    data: buildCreateAliasBodyRequest(params),
    hostname: params.hostname,
  })
}

export function confirmPurchase(params) {
  return requestByPOST({
    endpoint: END_POINTS.PURCHASE_CONFIRMATION,
    data: buildConfirmPurchaseBodyRequest(params),
    options: CONFIRMATION_OPTIONS,
    hostname: params.hostname,
  })
}

export function confirmPurchaseAuthorized(params) {
  return requestByPOST({
    endpoint: END_POINTS.PURCHASE_CONFIRMATION_AUTH,
    data: buildConfirmPurchaseBodyRequest(params),
    options: { ...CONFIRMATION_OPTIONS, withCredentials: true },
    hostname: params.hostname,
  })
}

export async function confirmWorldlinePurchase(params) {
  return requestByPOST({
    endpoint: END_POINTS.WORLDLINE_PURCHASE_CONFIRMATION,
    data: buildConfirmWorldlinePurchaseBodyRequest(params),
    options: CONFIRMATION_OPTIONS,
    hostname: params.hostname,
  })
}

export async function confirmWorldlinePurchaseAuthorized(params) {
  return requestByPOST({
    endpoint: END_POINTS.WORLDLINE_PURCHASE_CONFIRMATION_AUTH,
    data: buildConfirmWorldlinePurchaseBodyRequest(params),
    options: { ...CONFIRMATION_OPTIONS, withCredentials: true },
    hostname: params.hostname,
  })
}

export async function confirmWorldlinePurchase3ds(params) {
  return requestByGET({
    endpoint: END_POINTS.WORLDLINE_PURCHASE_CONFIRMATION_3DS,
    data: params,
    options: CONFIRMATION_OPTIONS_GET,
    hostname: params.hostname,
  })
}

export async function confirmPurchaseWithApplePay(params) {
  return requestByPOST({
    endpoint: END_POINTS.WORLDLINE_APPLEPAY_CONFIRMATION,
    data: buildConfirmApplePayPurchaseBodyRequest(params),
    options: CONFIRMATION_OPTIONS,
    hostname: params.hostname,
  })
}

export function confirmPurchaseWithApm(params) {
  return requestByPOST({
    endpoint: buildPathParams(END_POINTS.PURCHASE_CONFIRMATION_APM, {
      paymentProvider: params.paymentProvider,
    }),
    data: buildConfirmPurchaseBodyRequest(params),
    options: { ...CONFIRMATION_OPTIONS, withCredentials: true },
    hostname: params.hostname,
  })
}

export function generateQuotePriceDropProtection(params) {
  return requestByPOST({
    endpoint: END_POINTS.PRICE_DROP_PROTECTION_QUOTE_GENERATE,
    data: params,
    options: { ...OPTIONS, withCredentials: true },
    hostname: params.hostname,
  })
}

export function getPaymentStatus(params) {
  return requestByGET({
    endpoint: buildPathParams(END_POINTS.PURCHASE_STATUS, {
      purchaseId: params.purchaseId,
    }),
    options: OPTIONS_GET,
    hostname: params.hostname,
  })
}
